import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import {
  LayoutDashboard, FileText, ShieldAlert, Trash2,
  Settings, ChevronDown, ChevronRight, PenSquare, Menu, X, BookUser,
  RefreshCw, Loader2, WifiOff, Inbox, Send, Bell, BellOff, LogOut, UserCog, User
} from 'lucide-react';

const FOLDER_ITEMS = [
  { key: 'drafts', label: 'Bozze',   icon: FileText },
  { key: 'spam',   label: 'Spam',    icon: ShieldAlert },
  { key: 'trash',  label: 'Cestino', icon: Trash2 },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accounts, unreadCounts, openCompose, categoryCount, customCategories, hasReplied, syncNotification, dismissSyncNotif, refreshUnreadCounts, currentUser, isAdmin, logout } = useApp();
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  // Check new emails state
  const [checkState, setCheckState] = useState('idle'); // idle | checking | done | error
  const [newTotal, setNewTotal] = useState(0);
  const [checkErrors, setCheckErrors] = useState(0);
  const resetTimer = useRef(null);

  // Desktop notification permission
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const pollingRef = useRef(null);
  const lastSeenRef = useRef(null); // total unread count seen at last poll

  // Initialize lastSeen with current totalUnread once accounts are loaded
  // (so that we don't notify for already-existing unread mails on first mount)
  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    try {
      const perm = await Notification.requestPermission();
      setNotifPerm(perm);
      if (perm === 'granted') {
        new Notification('Mariani Inbox', {
          body: 'Notifiche attivate! Ti avviseremo all\'arrivo di nuove email.',
          icon: '/favicon.ico',
        });
      }
    } catch {}
  };

  const path = location.pathname;
  const isDashboard = path === '/dashboard';
  const isCategory = (cat) => path === `/category/${cat}`;

  const totalUnread = Object.values(unreadCounts).reduce((s, a) => s + (a.inbox || 0), 0);

  const getFolderCount = (folder) =>
    Object.values(unreadCounts).reduce((s, a) => s + (a[folder] || 0), 0);

  const handleCheckNew = useCallback(async () => {
    if (checkState === 'checking' || accounts.length === 0) return;
    if (resetTimer.current) clearTimeout(resetTimer.current);
    setCheckState('checking');
    setNewTotal(0);
    setCheckErrors(0);

    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    const token = localStorage.getItem('mariani_auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    let total = 0;
    let errors = 0;

    await Promise.allSettled(
      accounts.map(async (acc) => {
        try {
          const res = await fetch(`${API}/sync-new/${acc.id}/inbox`, { headers });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const data = await res.json();
          total += data.new || 0;
        } catch {
          errors++;
        }
      })
    );

    setNewTotal(total);
    setCheckErrors(errors);
    setCheckState(errors > 0 && total === 0 ? 'error' : 'done');

    if (total > 0) {
      await refreshUnreadCounts();
    }

    // Auto-reset to idle after 30s
    resetTimer.current = setTimeout(() => {
      setCheckState('idle');
      setNewTotal(0);
    }, 30000);
  }, [checkState, accounts, refreshUnreadCounts]);

  const CheckIcon = checkState === 'checking' ? Loader2 : checkState === 'error' ? WifiOff : RefreshCw;

  // Background polling every 30s to check new mails across all accounts.
  // When new mails arrive, refresh counts, broadcast event so EmailList reloads,
  // and show a desktop notification.
  useEffect(() => {
    if (accounts.length === 0) return;
    const API = process.env.REACT_APP_BACKEND_URL + '/api';

    const pollNewMails = async () => {
      const token = localStorage.getItem('mariani_auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let totalNew = 0;
      const perAccount = [];
      await Promise.allSettled(
        accounts.map(async (acc) => {
          try {
            const res = await fetch(`${API}/sync-new/${acc.id}/inbox`, { headers });
            if (!res.ok) return;
            const data = await res.json();
            const n = data.new || 0;
            if (n > 0) {
              totalNew += n;
              perAccount.push({ email: acc.email, count: n });
            }
          } catch {}
        })
      );
      if (totalNew > 0) {
        await refreshUnreadCounts();
        // Notify EmailList & other listeners to reload visible data
        window.dispatchEvent(new CustomEvent('mariani:new-mail', {
          detail: { total: totalNew, perAccount }
        }));
        // Show desktop notification if permitted
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          const title = totalNew === 1
            ? '1 nuova email — Mariani Inbox'
            : `${totalNew} nuove email — Mariani Inbox`;
          const body = perAccount
            .map(a => `${a.email}: ${a.count}`)
            .join('\n');
          try {
            const n = new Notification(title, {
              body,
              icon: '/favicon.ico',
              tag: 'mariani-new-mail',
              renotify: true,
            });
            n.onclick = () => { window.focus(); navigate('/inbox'); n.close(); };
          } catch {}
        }
      }
    };

    // Poll every 10 minutes
    pollingRef.current = setInterval(pollNewMails, 600000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [accounts, refreshUnreadCounts, navigate]);
  const checkIconColor = checkState === 'error' ? '#ef4444' : checkState === 'done' ? '#2DAA7E' : '#9CA3AF';

  const SidebarContent = () => (
    <div className="sidebar h-full flex flex-col w-[260px] flex-shrink-0" data-testid="sidebar">
      {/* Header */}
      <div className="px-4 py-5" style={{ borderBottom: '1px solid #D9D2C8' }}>
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
            style={{ background: '#E2725B', fontFamily: 'Inter, sans-serif' }}
          >
            MI
          </div>
          <div>
            <div className="font-semibold text-sm text-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>
              Mariani Inbox
            </div>
            <div className="text-xs text-gray-500">
              {accounts.length} account {accounts.length !== 1 ? 'attivi' : 'attivo'}
            </div>
          </div>
        </div>

        {/* User row */}
        {currentUser && (
          <div
            data-testid="sidebar-user-row"
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
            style={{ background: isAdmin ? 'rgba(226,114,91,0.10)' : '#F1F3F4' }}
          >
            {isAdmin
              ? <UserCog size={13} style={{ color: '#E2725B' }} className="flex-shrink-0" />
              : <User size={13} className="text-gray-500 flex-shrink-0" />
            }
            <span className="text-xs font-medium flex-1 capitalize" style={{ color: isAdmin ? '#E2725B' : '#4B5563' }}>
              {currentUser.username} {isAdmin && <span className="text-[10px] opacity-75">(admin)</span>}
            </span>
            <button
              data-testid="sidebar-logout-btn"
              onClick={async () => { await logout(); navigate('/'); }}
              className="p-1 rounded hover:bg-white/60 transition-colors"
              title="Esci"
            >
              <LogOut size={12} className="text-gray-500" />
            </button>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex-1 overflow-y-auto py-2">
        {/* Dashboard */}
        <div className="px-2 mb-2">
          <button
            data-testid="nav-dashboard"
            onClick={() => navigate('/dashboard')}
            className={`sidebar-item ${isDashboard ? 'active-dashboard' : ''}`}
          >
            <LayoutDashboard size={16} />
            <span>Dashboard</span>
          </button>
        </div>

        {/* CONTROLLA NUOVE MAIL */}
        {accounts.length > 0 && (
          <div className="px-2 mb-3">
            <button
              data-testid="nav-check-new-emails"
              onClick={handleCheckNew}
              disabled={checkState === 'checking'}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                background: checkState === 'done' && newTotal > 0
                  ? 'linear-gradient(135deg, #FFF0ED 0%, #FFE8E3 100%)'
                  : checkState === 'error'
                  ? '#FEF2F2'
                  : '#F5F1EC',
                border: `1.5px solid ${
                  checkState === 'done' && newTotal > 0 ? '#E2725B' :
                  checkState === 'error' ? '#EF4444' :
                  checkState === 'done' ? '#2DAA7E' : '#D9D2C8'
                }`,
                color: checkState === 'error' ? '#DC2626' : '#4B4540',
                cursor: checkState === 'checking' ? 'not-allowed' : 'pointer',
                opacity: checkState === 'checking' ? 0.8 : 1,
              }}
            >
              <CheckIcon
                size={15}
                className={checkState === 'checking' ? 'animate-spin' : ''}
                style={{ color: checkIconColor, flexShrink: 0 }}
              />
              <span className="flex-1 text-left">
                {checkState === 'checking' ? 'Controllo in corso…' :
                 checkState === 'error' ? 'Connessione fallita' :
                 'Controlla nuove mail'}
              </span>
              {checkState === 'done' && newTotal > 0 && (
                <span
                  className="text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                  style={{ background: '#E2725B', color: 'white' }}
                >
                  +{newTotal}
                </span>
              )}
              {checkState === 'done' && newTotal === 0 && (
                <span className="text-xs font-medium flex-shrink-0" style={{ color: '#2DAA7E' }}>
                  OK
                </span>
              )}
              {checkState === 'error' && checkErrors > 0 && (
                <span className="text-xs font-medium flex-shrink-0 text-red-500">
                  {checkErrors} err
                </span>
              )}
            </button>
          </div>
        )}

        {/* CATEGORIE */}
        <div className="sidebar-section-title">Categorie</div>
        {customCategories.map(cat => {
          const count = categoryCount?.[cat.id]?.unread || 0;
          return (
            <button
              key={cat.id}
              data-testid={`nav-cat-${cat.id}`}
              onClick={() => navigate(`/category/${cat.id}`)}
              className={`sidebar-item ${isCategory(cat.id) ? 'font-semibold' : ''}`}
              style={isCategory(cat.id) ? { background: 'rgba(0,0,0,0.05)' } : {}}
            >
              <span
                className="w-3 h-3 rounded-full border-2 flex-shrink-0"
                style={{ borderColor: cat.color, background: isCategory(cat.id) ? cat.color : 'transparent' }}
              />
              <span className="flex-1">{cat.name}</span>
              {count > 0 && (
                <span className="text-xs font-semibold" style={{ color: cat.color }}>{count}</span>
              )}
            </button>
          );
        })}

        {/* Mail in arrivo */}
        <button
          data-testid="nav-unified-inbox"
          onClick={() => navigate('/inbox')}
          className="sidebar-item"
          style={path === '/inbox' ? { background: 'rgba(0,0,0,0.05)' } : {}}
        >
          <span className="w-3 h-3 rounded-full border-2 flex-shrink-0" style={{ borderColor: '#E2725B', background: path === '/inbox' ? '#E2725B' : 'transparent' }} />
          <span className="flex-1">Mail in arrivo</span>
          {totalUnread > 0 && (
            <span
              data-testid="unread-badge-inbox"
              className="text-xs font-bold px-2 py-0.5 rounded-full text-white flex-shrink-0"
              style={{
                background: '#E2725B',
                minWidth: '22px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(226,114,91,0.4)',
              }}
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>

        {/* Inviati — voce prominente subito sotto "Tutte le mail" */}
        {(() => {
          const sentPath = accounts.length === 1
            ? `/account/${accounts[0]?.id}/sent`
            : '/folder/sent';
          const sentActive = path === sentPath || path === '/folder/sent' || path.endsWith('/sent');
          const sentCount = Object.values(unreadCounts).reduce((s, a) => s + (a.sent || 0), 0);
          return (
            <button
              data-testid="nav-sent-folder"
              onClick={() => navigate(sentPath)}
              className="sidebar-item"
              style={sentActive ? { background: 'rgba(0,0,0,0.05)', fontWeight: 600 } : {}}
            >
              <Send size={15} className="flex-shrink-0" style={{ color: sentActive ? '#E2725B' : '#6B7280' }} />
              <span className="flex-1" style={{ color: sentActive ? '#1a1a1a' : undefined }}>Inviati</span>
              {sentCount > 0 && (
                <span className="text-xs font-semibold" style={{ color: '#6B7280' }}>{sentCount}</span>
              )}
            </button>
          );
        })()}

        {/* CARTELLE */}
        <div className="sidebar-section-title mt-2">Cartelle</div>
        {FOLDER_ITEMS.map(({ key, label, icon: Icon }) => {
          const count = getFolderCount(key);
          const targetPath = accounts.length === 1
            ? `/account/${accounts[0]?.id}/${key}`
            : `/folder/${key}`;
          return (
            <button
              key={key}
              data-testid={`nav-folder-${key}`}
              onClick={() => navigate(targetPath)}
              className="sidebar-item"
              style={path.includes(`/${key}`) ? { background: 'rgba(0,0,0,0.05)' } : {}}
            >
              <Icon size={15} className="text-gray-500" />
              <span className="flex-1">{label}</span>
              {count > 0 && <span className="text-xs text-gray-500">{count}</span>}
            </button>
          );
        })}

        {/* ACCOUNT */}
        {accounts.length > 0 && (
          <>
            <div className="sidebar-section-title mt-2">Account</div>
            {accounts.map((acc) => {
              const count = unreadCounts[acc.id]?.inbox || 0;
              return (
                <button
                  key={acc.id}
                  data-testid={`nav-account-${acc.id}`}
                  onClick={() => navigate(`/account/${acc.id}/inbox`)}
                  className="sidebar-item"
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: acc.color }} />
                  <span className="flex-1 truncate text-gray-700">{acc.email}</span>
                  {count > 0 && (
                    <span className="text-xs font-semibold" style={{ color: acc.color }}>{count}</span>
                  )}
                </button>
              );
            })}
          </>
        )}
      </div>

      {/* Bottom */}
      <div className="p-2" style={{ borderTop: '1px solid #D9D2C8' }}>
        {/* Notifiche desktop */}
        {notifPerm !== 'unsupported' && (
          notifPerm === 'granted' ? (
            <div
              data-testid="notif-status-granted"
              className="sidebar-item rounded-lg"
              style={{ cursor: 'default' }}
              title="Notifiche desktop attive"
            >
              <Bell size={14} style={{ color: '#2DAA7E' }} className="flex-shrink-0" />
              <span className="text-gray-600 text-xs flex-1">Notifiche attive</span>
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: '#2DAA7E' }}
              />
            </div>
          ) : notifPerm === 'denied' ? (
            <div
              data-testid="notif-status-denied"
              className="sidebar-item rounded-lg"
              style={{ cursor: 'default' }}
              title="Notifiche bloccate dal browser — abilitale dalle impostazioni del sito"
            >
              <BellOff size={14} className="flex-shrink-0 text-gray-400" />
              <span className="text-gray-400 text-xs flex-1">Notifiche bloccate</span>
            </div>
          ) : (
            <button
              data-testid="enable-notif-btn"
              onClick={requestNotifPermission}
              className="sidebar-item rounded-lg w-full"
              title="Ricevi avvisi desktop quando arrivano nuove email"
            >
              <Bell size={14} style={{ color: '#E2725B' }} className="flex-shrink-0" />
              <span className="text-gray-700 text-xs flex-1 text-left">Attiva notifiche</span>
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0"
                style={{ background: '#FFF0EC', color: '#E2725B' }}
              >
                ON
              </span>
            </button>
          )
        )}

        {accounts.length > 0 && (
          <button
            data-testid="nav-rubrica"
            onClick={() => { dismissSyncNotif(); navigate('/rubrica'); }}
            className={`sidebar-item rounded-lg ${path === '/rubrica' ? 'bg-white shadow-sm font-medium' : ''}`}
          >
            <span className="relative flex-shrink-0">
              <BookUser size={15} style={{ color: '#2DAA7E' }} />
              {syncNotification && (
                <span
                  data-testid="rubrica-sync-badge"
                  className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full"
                  style={{ background: '#2DAA7E', boxShadow: '0 0 0 0 rgba(45,170,126,0.6)', animation: 'ping-green 1.2s cubic-bezier(0,0,0.2,1) infinite' }}
                />
              )}
            </span>
            <span className="text-gray-600">Rubrica</span>
            {syncNotification && (
              <span
                className="ml-auto text-xs font-semibold px-1.5 py-0.5 rounded-full"
                style={{ background: '#E4F7EE', color: '#2DAA7E' }}
              >
                Aggiornata
              </span>
            )}
          </button>
        )}
        <button
          data-testid="nav-settings"
          onClick={() => navigate('/settings')}
          className={`sidebar-item rounded-lg ${path === '/settings' ? 'bg-white shadow-sm font-medium' : ''}`}
        >
          <Settings size={15} className="text-gray-500" />
          <span className="text-gray-600">Impostazioni</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg shadow-md"
        style={{ background: '#E2725B', color: 'white' }}
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="sidebar-toggle-mobile"
      >
        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40" onClick={() => setMobileOpen(false)}
          style={{ background: 'rgba(0,0,0,0.3)' }}>
          <div onClick={e => e.stopPropagation()} className="h-full w-[260px]">
            <SidebarContent />
          </div>
        </div>
      )}

      <div className="hidden md:block">
        <SidebarContent />
      </div>
    </>
  );
}
